<template lang="pug">
  .light-works-pdp(itemprop="product" itemscope itemtype="http://schema.org/Product")
    PdpBanner

    template(v-if='hasActiveSub')
      PdpExistingSubBanner(:quiz-completed='lightWorksQuizCompleted' quiz-url='/color-advisor?rectype=lw' more-colors-url='/shopping/light-works-highlights')

    template(v-else)
      .container
        .back-to-advisor.xs-12.text-center.xs-pt-100m(v-if="lightWorksQuizCompleted")
          p.xs-f-small.lg-f-medium.max-at-tweak
            | Drumroll please... your&nbsp;
            a.underline(href='/color-advisor?rectype=lw' target='_self') quiz results
            |  are in!

    FixedWidthContainer
      LightWorksHero.xs-pt-100m

    FixedWidthContainer
      PdpInfoBullets(:bullets="product.prodInfo")

    //- ingredients
    LazyComponent
      template(#default="{ shown }")
        FixedWidthContainer(v-if="shown")
          AccordionSection(title="Ingredients" @accordion-toggle="trackAccordion" :border-top="true" :border-bottom="true")
            //- Accordion slot content
            .xs-12.xs-pt-200m.xs-pr-150m.xs-pb-150m.xs-pl-150m.sm-pt-50m.sm-pr-100m.sm-pb-100m.sm-pl-100m
              PdpIngredients(v-if="product.ingredientsFactsheet" :ingredients="product.ingredientsFactsheet")

    LazyComponent
      template(#default="{ shown }")
        FixedWidthContainer(v-if="shown && questionsAndAnswers && questionsAndAnswers.length")
          AccordionSection(title="Q&A" @accordion-toggle="trackAccordion")
            .xs-12.xs-pl-100m.xs-pt-150m.xs-pb-150m.text-center
              h3.semi-bold.xs-f-small.capitalize.max-at-tweak.text-center.xs-mb-150m(v-if="$root.windowData.breakpoint === 'mobile'") Q&amp;A
              a.mr-btn.btn-inverse.xs-f-xsmall.max-at-tweak(href="/support") ask a question
              QuestionAndAnswer(v-for="(qa, index) in questionsAndAnswers" :question="qa.question" :answer="qa.answer" :key="index")

    // UGC Images
    .row
      DashHudsonScript
        template(#title="")
          h3.bold.upper.xs-f-small.md-f-small.lg-f-large.max-at-tweak.text-center Real clients, really gorgeous results

    //- Reviews
    Reviews(:productId="product.id" :productUrlKey="product.url_key")

    LazyComponent
      template(#default="{ shown }")
        .what-to-know-wrap(v-if="shown")
          FixedWidthContainer
            .info-section.xs-12
              .xs-12.md-8.md-push-2.lg-push-0.lg-6.text-center
                SectionTitle.xs-mt-150m.xs-mb-100m {{ whatToKnow.title }}
                p.xs-f-small.lg-f-medium.max-at-tweak.xs-pb-150m.sm-pr-100m.sm-pl-100m(v-html="whatToKnow.text")

              .xs-12.lg-6.xs-f-small.lg-f-small.max-at-tweak
                .video-wrap(v-if="whatToKnow.videoUrl")
                  MrVideo(:mediaObj="{ url: whatToKnow.videoUrl, title: whatToKnow.videoTitle, video_thumb: whatToKnow.videoImage.url }")

    LazyComponent
      template(#default="{ shown }")
        FixedWidthContainer(v-if="shown")
          .info-section.xs-12
            .xs-12.md-8.md-push-2.lg-push-0.lg-6.text-center.lg-right
              SectionTitle.xs-mt-150m.xs-mb-100m {{ highlightsReinvented.title }}
              p.xs-f-small.lg-f-medium.max-at-tweak.xs-pb-150m.sm-pr-100m.sm-pl-100m(v-html="highlightsReinvented.text")

            .xs-12.lg-6
              .a-box-xs-16x9
                .a-box-content
                  img-box(:mediaId="highlightsReinvented.image.mediaId")

    //- Contact color crew
    LazyComponent
      template(#default="{ shown }")
        FixedWidthContainer(v-if="shown")
          ExpertiseIncluded.row.xs-pt-100m

    //- Satisfaction
    LazyComponent
      template(#default="{ shown }")
        LockupBanner(v-if="shown" :cms="cmsSettings.moneyBackGuarantee")

    //- Xsell
    LazyComponent
      template(#default="{ shown }")
        .container.xs-mt-400m(v-if="shown && remainingXsellItemsAvailable.length > 0")
          .row.text-center
            SectionTitle.xs-pb-25m Recommended
            span.text-color-2.xs-f-medium.lg-f-xmedium.max-at-tweak with {{ product.name }}
            PdpXsellCarousel(:items="remainingXsellItemsAvailable")

</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex';
  import { defineAsyncComponent } from 'vue';
  import LazyComponent from '../../LazyComponent';
  import FixedWidthContainer from '../../FixedWidthContainer';
  import PdpBanner from '../PdpBanner';
  import LightWorksHero from './LightWorksHero/LightWorksHero';
  import Reviews from '../Reviews/Reviews';
  import PdpInfoBullets from '../PdpInfoBullets/PdpInfoBullets';
  import PdpExistingSubBanner from "../PdpExistingSubBanner/PdpExistingSubBanner";
  import PdpIngredients from '../PdpIngredients';
  import DashHudsonScript from "@components/PDP/DashHudsonScript.vue";

  export default {
    name: 'LightWorksPdp',

    components: {
      DashHudsonScript,
      PdpExistingSubBanner,
      MrVideo: defineAsyncComponent(() => import('../../mrVideo')),
      LazyComponent,
      PdpBanner,
      PdpInfoBullets,
      LightWorksHero,
      LeapingBunny: defineAsyncComponent(() => import('../LeapingBunny/LeapingBunny')),
      QuestionAndAnswer: defineAsyncComponent(() => import('../QuestionAndAnswer/QuestionAndAnswer')),
      AccordionSection: defineAsyncComponent(() => import('../AccordionSection/AccordionSection')),
      ExpertiseIncluded: defineAsyncComponent(() => import('../../ExpertiseIncluded/ExpertiseIncluded')),
      LockupBanner: defineAsyncComponent(() => import('../../banners/lockupBanner')),
      PdpXsellCarousel: defineAsyncComponent(() => import('../PdpXsellCarousel/PdpXsellCarousel')),
      SectionTitle: defineAsyncComponent(() => import('../../SectionTitle')),
      PdpIngredients,
      Reviews,
      FixedWidthContainer,
    },

    inject: ['getObjProperty'],

    computed: {
      ...mapState('pdp', ['product']),
      ...mapState('cms', ['cmsSettings']),
      ...mapState('colorAdvisor', ['answers', 'lightWorksQuizCompleted']),
      ...mapState('xsell', ['productXsellItems']),
      ...mapState('cart', ['cart']),
      ...mapState('customer', ['cdata']),
      ...mapGetters('pdp', ['hasActiveSub']),

      highlightsReinvented() {
        return (
          this.getObjProperty(this.cmsSettings, 'highlightsReinvented.settings') || { image: {} }
        );
      },

      whatToKnow() {
        return this.getObjProperty(this.cmsSettings, 'whatToKnow.settings') || { image: {} };
      },

      level() {
        return this.answers.hair_shade || this.answers.natural_hair_shade;
      },

      questionsAndAnswers() {
        let productTypeQuestions = this.getObjProperty(this, 'product.productTypeFaqs') || [];
        let productQuestions = this.getObjProperty(this, 'product.productFaqs') || [];
        return productTypeQuestions.concat(productQuestions);
      },

      remainingXsellItemsAvailable() {
        return this.productXsellItems.filter(product => !this.cart.items.find(item => item.id === product.id));
      }
    },

    mounted() {
      if (this.$route.query.purchasePanel || this.$route.hash == '#purchase-modal') {
        this.openPurchasePanelModal({
          theme: 'full-screen'
        });
      }
      this.initColorAdvisor();
      this.isLightWorksQuizCompleted();
    },

    methods: {
      ...mapActions('colorAdvisor', [
        'initColorAdvisor',
        'isLightWorksQuizCompleted'
      ]),

      ...mapActions('pdp', [
        'openPurchasePanelModal',
      ]),

      trackAccordion(data) {
        this.trackMREvent(`${data.title} Section Toggled`, {
          product_name: this.product.name,
          product_code: this.product.code,
          product_id: this.product.id,
        });
      },
    },
  };
</script>

<style scoped lang="stylus">
  .light-works-pdp
    .expertise-included
      padding-top 3em
      border-top 1px solid #cbc8c7

    .review-wrap
      .reviews
        border-bottom 1px solid #cbc8c7
        padding-bottom 2em
        margin-bottom 3em

      .title-wrap
        padding 1.5em 0

        .section-title
          padding-bottom 0.6em

        .desc-short
          padding-bottom 0.5em

    .what-to-know-wrap
      background-color #ebebeb

      .video-wrap
        max-width 32em
        height 18em
        margin 0 auto

    .info-section
      padding 3em 0

    @media mq-tablet-plus
      .fixed-width-tweak-upper
        padding-left 3%
        padding-right 3%
</style>
